import React, { useState } from "react";
import {
  Box,
  Typography,
  Button,
  FormControl,
  MenuItem,
  Select,
  TextField,
  Checkbox,
  FormControlLabel,
  Divider,
  Tooltip,
} from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useTranslation } from "react-i18next";

import { ALL_OPTION_VALUE } from "../constants";
import { isValidDecimalInput } from "../utils";

export default function RxGhgFilters({
  materialOptions,
  donorOptions,
  draftMaterials,
  setDraftMaterials,
  draftDonors,
  setDraftDonors,
  draftDonorSearch,
  setDraftDonorSearch,
  draftReceiverSearch,
  setDraftReceiverSearch,
  draftReceiverOnlyUsed,
  setDraftReceiverOnlyUsed,
  draftEfMin,
  setDraftEfMin,
  draftEfMax,
  setDraftEfMax,
  draftReMin,
  setDraftReMin,
  draftReMax,
  setDraftReMax,
  draftNetMin,
  setDraftNetMin,
  draftNetMax,
  setDraftNetMax,
  draftQtyMin,
  setDraftQtyMin,
  draftQtyMax,
  setDraftQtyMax,
  draftAvoidedMin,
  setDraftAvoidedMin,
  draftAvoidedMax,
  setDraftAvoidedMax,
  onApply,
  onClear,
}) {
  const { t } = useTranslation();
  const [showAdvanced, setShowAdvanced] = useState(false);

  const normalizeMultiSelectValue = (value) => {
    if (Array.isArray(value)) return value;
    if (typeof value === "string") return value.split(",").filter(Boolean);
    return [];
  };

  const handleMultiWithAll = (incoming, setter) => {
    const next = normalizeMultiSelectValue(incoming);
    if (next.includes(ALL_OPTION_VALUE)) {
      setter([]);
      return;
    }
    setter(next);
  };

  const RangePair = ({
    minVal,
    maxVal,
    onMin,
    onMax,
    minPh,
    maxPh,
  }) => (
    <Box className="rxghg_rng">
      <TextField
        size="small"
        fullWidth
        value={minVal}
        onChange={(e) => {
          const v = e.target.value;
          if (isValidDecimalInput(v)) onMin(v);
        }}
        placeholder={minPh ?? t("ghg.filters.min")}
        className="rxghg_inp"
        inputProps={{ inputMode: "decimal" }}
      />
      <TextField
        size="small"
        fullWidth
        value={maxVal}
        onChange={(e) => {
          const v = e.target.value;
          if (isValidDecimalInput(v)) onMax(v);
        }}
        placeholder={maxPh ?? t("ghg.filters.max")}
        className="rxghg_inp"
        inputProps={{ inputMode: "decimal" }}
      />
    </Box>
  );

  return (
    <Box className="rxghg_filtersShell">
      <Box className="rxghg_filtersTop">
        <Box className="rxghg_filtersTitle">
          <FilterAltIcon fontSize="small" />
          <Typography className="rxghg_filtersTitleTxt">
            {t("ghg.filters.title")}
          </Typography>

          <Tooltip title={t("ghg.filters.tooltip")}>
            <span className="rxghg_infoDot">
              <InfoOutlinedIcon fontSize="inherit" />
            </span>
          </Tooltip>
        </Box>

        <Box className="rxghg_filtersActions">
          <Button
            className="rxghg_btn rxghg_btnGhost"
            size="small"
            onClick={onClear}
          >
            {t("ghg.filters.clear")}
          </Button>
          <Button
            className="rxghg_btn"
            size="small"
            variant="contained"
            onClick={onApply}
          >
            {t("ghg.filters.apply")}
          </Button>
        </Box>
      </Box>

      <Box className="rxghg_filtersGridBasic">
        {/* Materials */}
        <FormControl size="small" fullWidth className="rxghg_inp">
          <Select
            multiple
            displayEmpty
            value={draftMaterials}
            onChange={(e) => handleMultiWithAll(e.target.value, setDraftMaterials)}
            renderValue={(selected) => {
              const s = Array.isArray(selected) ? selected : [];
              if (s.length === 0) return t("ghg.filters.materials");
              return t("ghg.filters.materialsCount", { count: s.length });
            }}
          >
            <MenuItem value={ALL_OPTION_VALUE}>
              <em>{t("ghg.filters.allMaterials")}</em>
            </MenuItem>
            {materialOptions.map((m) => (
              <MenuItem key={m} value={m}>
                {m}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Donors */}
        <FormControl size="small" fullWidth className="rxghg_inp">
          <Select
            multiple
            displayEmpty
            value={draftDonors}
            onChange={(e) => handleMultiWithAll(e.target.value, setDraftDonors)}
            renderValue={(selected) => {
              const s = Array.isArray(selected) ? selected : [];
              if (s.length === 0) return t("ghg.filters.donors");
              return t("ghg.filters.donorsCount", { count: s.length });
            }}
          >
            <MenuItem value={ALL_OPTION_VALUE}>
              <em>{t("ghg.filters.allDonors")}</em>
            </MenuItem>
            {donorOptions.map((d) => (
              <MenuItem key={d} value={d}>
                {d}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <TextField
          size="small"
          fullWidth
          value={draftDonorSearch}
          onChange={(e) => setDraftDonorSearch(e.target.value)}
          placeholder={t("ghg.filters.searchDonorRows")}
          className="rxghg_inp"
        />

        <TextField
          size="small"
          fullWidth
          value={draftReceiverSearch}
          onChange={(e) => setDraftReceiverSearch(e.target.value)}
          placeholder={t("ghg.filters.searchReceiverFactors")}
          className="rxghg_inp"
        />

        <FormControlLabel
          className="rxghg_toggle"
          control={
            <Checkbox
              checked={draftReceiverOnlyUsed}
              onChange={(e) => setDraftReceiverOnlyUsed(e.target.checked)}
              size="small"
            />
          }
          label={t("ghg.filters.usedOnly")}
        />
      </Box>

      <Box className="rxghg_advToggleRow">
        <Button
          className="rxghg_linkBtn"
          size="small"
          onClick={() => setShowAdvanced((p) => !p)}
        >
          {showAdvanced
            ? t("ghg.filters.hideAdvanced")
            : t("ghg.filters.showAdvanced")}
        </Button>

        <Typography className="rxghg_hintTxt">
          {t("ghg.filters.decimalsOk")}
        </Typography>
      </Box>

      {showAdvanced ? (
        <>
          <Divider className="rxghg_div" />
          <Box className="rxghg_filtersGridAdv">
            <Box className="rxghg_advItem">
              <Typography className="rxghg_advLabel">
                {t("ghg.filters.emissionFactor")}
              </Typography>
              <RangePair
                minVal={draftEfMin}
                maxVal={draftEfMax}
                onMin={setDraftEfMin}
                onMax={setDraftEfMax}
              />
            </Box>

            <Box className="rxghg_advItem">
              <Typography className="rxghg_advLabel">
                {t("ghg.filters.recyclingEmissions")}
              </Typography>
              <RangePair
                minVal={draftReMin}
                maxVal={draftReMax}
                onMin={setDraftReMin}
                onMax={setDraftReMax}
              />
            </Box>

            <Box className="rxghg_advItem">
              <Typography className="rxghg_advLabel">
                {t("ghg.filters.netAvoidedFactor")}
              </Typography>
              <RangePair
                minVal={draftNetMin}
                maxVal={draftNetMax}
                onMin={setDraftNetMin}
                onMax={setDraftNetMax}
              />
            </Box>

            <Box className="rxghg_advItem">
              <Typography className="rxghg_advLabel">
                {t("ghg.filters.qty")}
              </Typography>
              <RangePair
                minVal={draftQtyMin}
                maxVal={draftQtyMax}
                onMin={setDraftQtyMin}
                onMax={setDraftQtyMax}
              />
            </Box>

            <Box className="rxghg_advItem">
              <Typography className="rxghg_advLabel">
                {t("ghg.filters.avoidedEmissions")}
              </Typography>
              <RangePair
                minVal={draftAvoidedMin}
                maxVal={draftAvoidedMax}
                onMin={setDraftAvoidedMin}
                onMax={setDraftAvoidedMax}
              />
            </Box>
          </Box>
        </>
      ) : null}
    </Box>
  );
}
